import { LoaderCircle } from '@parrot-software/design-system/icons';
import { useTranslation } from 'react-i18next';

import { ParrotLogo } from '@experimental-components/IconsComponents/ParrotLogo';

import { useSpinnerDelay } from '@identity/presentation/components/base/FullScreenLoader/useSpinnerDelay';

/**
 * How long the wait stays hidden before showing the spinner. Below it the redirect has already
 * happened, and a spinner that flashes looks worse than no indicator at all.
 */
export const SPINNER_DELAY_MS = 200;

/**
 * Full-screen waiting view of the flow.
 *
 * It shows no visible text: the screen usually lasts under a second, and a message that appears and
 * disappears reads as a glitch. The state is still announced from the first frame, so the delay
 * hides the spinner from the eye and not from assistive technology.
 *
 * The delay belongs to the wait and not to this mount, so two loaders in a row hand the spinner over
 * with no empty frame in between.
 */
export const FullScreenLoader = () => {
  const { t } = useTranslation('identity');
  const isSpinnerVisible = useSpinnerDelay(SPINNER_DELAY_MS);

  return (
    <div
      aria-busy="true"
      className="flex min-h-screen w-full flex-col items-center justify-center gap-8 bg-background font-sans"
      data-testid="identity-full-screen-loader"
    >
      <span className="sr-only">{t('loader.srOnly')}</span>
      {isSpinnerVisible && (
        <>
          <ParrotLogo aria-hidden className="h-20" />
          <LoaderCircle
            aria-hidden
            className="size-10 animate-spin text-primary"
            data-testid="identity-loader-spinner"
          />
        </>
      )}
    </div>
  );
};
